/**
 * useOnboarding Hook
 *
 * React Query hook for managing onboarding state.
 * Fetches the current onboarding step and provides mutation
 * to advance to the next step.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../services/api";
import type {
  OnboardingState,
  OnboardingStepUpdate,
} from "../types/onboarding";

const API_PREFIX = import.meta.env.VITE_API_V1_PREFIX || "/api/v1";

async function getOnboardingState(): Promise<OnboardingState> {
  const response = await apiClient.get<OnboardingState>(
    `${API_PREFIX}/users/me/onboarding`
  );
  return response.data;
}

async function updateOnboardingStep(
  data: OnboardingStepUpdate
): Promise<OnboardingState> {
  const response = await apiClient.patch<OnboardingState>(
    `${API_PREFIX}/users/me/onboarding`,
    data
  );
  return response.data;
}

export function useOnboarding(options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();

  const query = useQuery<OnboardingState, Error>({
    queryKey: ["onboarding"],
    queryFn: getOnboardingState,
    staleTime: 30 * 1000, // 30 seconds
    enabled: options?.enabled !== false,
  });

  const advanceStep = useMutation<OnboardingState, Error, string>({
    mutationFn: (step: string) => updateOnboardingStep({ step }),
    onSuccess: (updatedState) => {
      queryClient.setQueryData(["onboarding"], updatedState);
      // Invalidate current user since onboarding_step is on User
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
    },
  });

  return {
    ...query,
    currentStep: query.data?.step ?? null,
    isOnboardingComplete: query.data?.step === "completed",
    steps: query.data?.steps ?? [],
    advanceStep,
  };
}
